import React, { useMemo, useState } from 'react';
import Layout from '../../../components/Layout';

/* ============================================================================
 * MOCKUP — Sezione "Righe preventivo": tre proposte di drill-down a 3 livelli
 *
 * Tutti i dati sono hardcoded. Nessuna chiamata API.
 * Riflette le decisioni concordate con Paolo:
 *  - 3 livelli di raggruppamento con etichette LIBERE
 *  - Drag & drop totale (righe + gruppi) — qui solo simulato visivamente
 *  - 3 proposte di drill-down:
 *      A) Accordion: tutto in pagina, gruppi espandibili/comprimibili
 *      B) Drill-in: clicchi sul gruppo ed entri "dentro" (breadcrumb in cima)
 *      C) Misto: accordion + bottone "focalizza" per drill-in puntuale
 *
 *  Lo switch tra le 3 viste è in cima alla pagina (solo per il mockup).
 * ============================================================================ */

// ── Icone ───────────────────────────────────────────────────────────────────

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const FocusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7V3h4" /><path d="M21 7V3h-4" /><path d="M3 17v4h4" /><path d="M21 17v4h-4" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const GripIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
    <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
  </svg>
);
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const ArrowLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);

// ── Dataset condiviso ───────────────────────────────────────────────────────

interface Riga {
  id: number;
  ordine: number;
  descrizione: string;
  codice?: string;
  um?: string;
  qta?: number;
  prezzo?: number;
  sconto?: number;
  importo?: number;
  alternativaGruppo?: string;     // "A", "B" — null se non alternativa
  alternativaScelta?: boolean;
  etichettaAlternativa?: string;
  tipo: 'ARTICOLO' | 'ARTICOLO_CONFIGURATO' | 'DESCRITTIVA';
}

interface Gruppo {
  id: number;
  livello: 1 | 2 | 3;
  parentId: number | null;
  ordine: number;
  etichetta: string;
  righe: Riga[];      // righe direttamente sotto questo gruppo
}

// 3 livelli, etichette libere, dati realistici Ceramita
const GRUPPI: Gruppo[] = [
  // ── Macro-area 1: Zona giorno (livello 1)
  { id: 1, livello: 1, parentId: null, ordine: 1, etichetta: 'Zona giorno', righe: [] },
  //   ├─ Locale: Soggiorno (livello 2)
  { id: 11, livello: 2, parentId: 1, ordine: 1, etichetta: 'Soggiorno', righe: [
    {
      id: 1001, ordine: 1, tipo: 'ARTICOLO_CONFIGURATO',
      codice: 'PC-PAVI-A', descrizione: 'Pavimento gres porcellanato 60x60 — Tortora',
      um: 'mq', qta: 28, prezzo: 38.50, sconto: 0, importo: 1078.00,
    },
  ]},
  //     ├─ Sezione: Pavimentazione (livello 3) — con cluster alternative
  { id: 111, livello: 3, parentId: 11, ordine: 1, etichetta: 'Battiscopa e accessori', righe: [
    {
      id: 1002, ordine: 1, tipo: 'ARTICOLO',
      codice: 'BATT-001', descrizione: 'Battiscopa MDF laccato bianco h.7',
      um: 'ml', qta: 22, prezzo: 4.20, sconto: 0, importo: 92.40,
    },
    {
      id: 1003, ordine: 2, tipo: 'ARTICOLO',
      codice: 'PROF-INOX', descrizione: 'Profilo terminale acciaio inox',
      um: 'pz', qta: 4, prezzo: 12.00, sconto: 5, importo: 45.60,
    },
  ]},
  //   ├─ Locale: Cucina (livello 2)
  { id: 12, livello: 2, parentId: 1, ordine: 2, etichetta: 'Cucina', righe: [
    {
      id: 1010, ordine: 1, tipo: 'DESCRITTIVA',
      descrizione: 'Fornitura e posa rivestimento parete cottura, h. 60 cm dal piano lavoro.',
    },
    {
      id: 1011, ordine: 2, tipo: 'ARTICOLO',
      codice: 'RIV-CUC-X1', descrizione: 'Rivestimento ceramico 25x40 — Bianco lucido',
      um: 'mq', qta: 6, prezzo: 22.00, sconto: 0, importo: 132.00,
      alternativaGruppo: 'A', alternativaScelta: true, etichettaAlternativa: 'Standard',
    },
    {
      id: 1012, ordine: 3, tipo: 'ARTICOLO',
      codice: 'RIV-CUC-X2', descrizione: 'Rivestimento ceramico 25x40 — Decoro Floreale',
      um: 'mq', qta: 6, prezzo: 34.00, sconto: 0, importo: 204.00,
      alternativaGruppo: 'A', alternativaScelta: false, etichettaAlternativa: 'Premium',
    },
  ]},

  // ── Macro-area 2: Zona notte (livello 1)
  { id: 2, livello: 1, parentId: null, ordine: 2, etichetta: 'Zona notte', righe: [] },
  //   ├─ Locale: Camera matrimoniale (livello 2)
  { id: 21, livello: 2, parentId: 2, ordine: 1, etichetta: 'Camera matrimoniale', righe: [
    {
      id: 2001, ordine: 1, tipo: 'ARTICOLO_CONFIGURATO',
      codice: 'PC-PARQ-RV', descrizione: 'Parquet prefinito rovere 14mm',
      um: 'mq', qta: 18, prezzo: 62.00, sconto: 5, importo: 1060.20,
    },
  ]},
  //   ├─ Locale: Bagno padronale (livello 2)
  { id: 22, livello: 2, parentId: 2, ordine: 2, etichetta: 'Bagno padronale', righe: [] },
  //     ├─ Sezione: Pavimento (livello 3)
  { id: 221, livello: 3, parentId: 22, ordine: 1, etichetta: 'Pavimento', righe: [
    {
      id: 2210, ordine: 1, tipo: 'ARTICOLO',
      codice: 'PAV-MARM', descrizione: 'Gres effetto marmo 60x120',
      um: 'mq', qta: 8, prezzo: 48.00, sconto: 0, importo: 384.00,
    },
  ]},
  //     ├─ Sezione: Rivestimento (livello 3)
  { id: 222, livello: 3, parentId: 22, ordine: 2, etichetta: 'Rivestimento doccia', righe: [
    {
      id: 2220, ordine: 1, tipo: 'ARTICOLO',
      codice: 'RIV-DOC', descrizione: 'Mosaico 30x30 — Beige lucido',
      um: 'mq', qta: 4.5, prezzo: 95.00, sconto: 10, importo: 384.75,
    },
  ]},
];

// Helpers struttura
function figliDi(parentId: number | null, livello: 1 | 2 | 3): Gruppo[] {
  return GRUPPI
    .filter(g => g.parentId === parentId && g.livello === livello)
    .sort((a, b) => a.ordine - b.ordine);
}
function totaleRigheRicorsivo(gruppoId: number): number {
  const g = GRUPPI.find(x => x.id === gruppoId);
  if (!g) return 0;
  let n = g.righe.length;
  for (const figlio of GRUPPI.filter(x => x.parentId === gruppoId)) {
    n += totaleRigheRicorsivo(figlio.id);
  }
  return n;
}
function importoRicorsivo(gruppoId: number): number {
  const g = GRUPPI.find(x => x.id === gruppoId);
  if (!g) return 0;
  let tot = g.righe
    .filter(r => !r.alternativaGruppo || r.alternativaScelta)
    .reduce((s, r) => s + (r.importo || 0), 0);
  for (const figlio of GRUPPI.filter(x => x.parentId === gruppoId)) {
    tot += importoRicorsivo(figlio.id);
  }
  return tot;
}
function fmt(n: number | undefined): string {
  if (n == null) return '';
  return n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Stili condivisi ─────────────────────────────────────────────────────────

const STYLE_BLOCK = `
.mockup-drilldown { padding: 16px 20px; }

.mockup-switch {
  display: flex; gap: 8px; margin-bottom: 24px;
  padding: 6px; background: var(--bop-color-surface-alt, #f1f5f9); border-radius: 10px;
  width: fit-content;
}
.mockup-switch button {
  border: 0; background: transparent; padding: 8px 16px; border-radius: 6px;
  font: 600 13px var(--bop-font-base, Inter, sans-serif); color: var(--bop-color-text-muted, #64748b);
  cursor: pointer; transition: all .15s ease;
}
.mockup-switch button.active {
  background: white; color: var(--bop-color-primary, #1e3a8a);
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.mockup-section-title {
  font-size: 13px; font-weight: 600; text-transform: uppercase;
  letter-spacing: .04em; color: var(--bop-color-text-muted, #64748b);
  margin: 0 0 8px 4px;
}
.mockup-note {
  background: #fffbeb; border-left: 3px solid #f59e0b; padding: 10px 14px;
  font-size: 13px; color: #78350f; border-radius: 4px; margin-bottom: 16px;
}

/* === STILE COMUNE PER GRUPPI === */
.gp-l1, .gp-l2, .gp-l3 {
  border: 1px solid var(--bop-color-border, #e2e8f0);
  border-radius: 8px; background: white; margin-bottom: 8px; overflow: hidden;
}
.gp-l1 { border-left: 4px solid #1e3a8a; }
.gp-l2 { border-left: 4px solid #6366f1; }
.gp-l3 { border-left: 4px solid #94a3b8; }

.gp-header {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; cursor: pointer;
  user-select: none;
  transition: background .12s ease;
}
.gp-header:hover { background: #f8fafc; }
.gp-l1 .gp-header { background: #eef2ff; font-weight: 600; }
.gp-l2 .gp-header { background: #f5f3ff; }
.gp-l3 .gp-header { background: #f8fafc; font-style: italic; }

.gp-grip { color: #cbd5e1; cursor: grab; display: flex; }
.gp-grip:hover { color: #64748b; }
.gp-chevron { color: #64748b; display: flex; }
.gp-label { flex: 1; font-size: 14px; color: #0f172a; }
.gp-l1 .gp-label { font-size: 15px; }
.gp-meta { font-size: 12px; color: #64748b; }
.gp-actions { display: flex; gap: 4px; opacity: 0; transition: opacity .12s ease; }
.gp-header:hover .gp-actions { opacity: 1; }
.gp-actions .bop-btn-icon { padding: 4px; }

.gp-body { padding: 0 12px 10px 12px; }
.gp-body--nested { padding-left: 28px; }
.gp-empty {
  padding: 12px 16px; color: #94a3b8; font-size: 13px; font-style: italic;
}
.gp-empty-add {
  padding: 8px 12px; margin: 4px 0;
  background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 6px;
  display: flex; gap: 8px; align-items: center;
  font-size: 12px; color: #64748b; cursor: pointer;
}
.gp-empty-add:hover { background: #f1f5f9; border-color: #94a3b8; }

/* === TABELLA RIGHE === */
.righe-table {
  width: 100%; border-collapse: collapse; font-size: 13px;
}
.righe-table th, .righe-table td {
  padding: 6px 8px; border-bottom: 1px solid #f1f5f9; text-align: left;
}
.righe-table th {
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .03em;
  color: #64748b; background: #f8fafc;
}
.righe-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
.righe-table tr.riga-row:hover { background: #fafbfc; }
.righe-table tr.riga-row { cursor: grab; }
.col-grip { width: 24px; color: #cbd5e1; }
.col-grip:hover { color: #64748b; }
.col-num { width: 30px; color: #94a3b8; font-size: 11px; }
.col-tipo { width: 70px; }
.col-codice { width: 110px; font-family: var(--bop-font-mono, 'JetBrains Mono', monospace); font-size: 11.5px; color: #475569; }
.col-um { width: 50px; }
.col-azioni { width: 80px; text-align: right; }
.tipo-badge {
  display: inline-block; padding: 2px 6px; border-radius: 3px;
  font-size: 10px; font-weight: 600; text-transform: uppercase;
}
.tipo-badge--ART { background: #dbeafe; color: #1e3a8a; }
.tipo-badge--CFG { background: #f0fdf4; color: #166534; }
.tipo-badge--DSC { background: #fef3c7; color: #78350f; }

/* Cluster alternative header */
.cluster-row td {
  background: #fff7ed; padding: 6px 12px; font-size: 12px; color: #9a3412;
  border-top: 1px solid #fed7aa;
}
.alt-badge {
  display: inline-block; padding: 1px 5px; border-radius: 2px;
  background: #f97316; color: white; font-size: 10px; font-weight: 700; margin-right: 6px;
}
.scelta-badge {
  display: inline-block; padding: 1px 5px; border-radius: 2px;
  background: #16a34a; color: white; font-size: 9px; font-weight: 700; margin-left: 6px;
}

/* === BREADCRUMB drill-in (B + C) === */
.breadcrumb-bar {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 10px 14px; background: white;
  border: 1px solid var(--bop-color-border, #e2e8f0); border-radius: 8px;
  margin-bottom: 12px;
}
.breadcrumb-back {
  display: inline-flex; align-items: center; gap: 4px;
  background: transparent; border: 0; padding: 4px 8px;
  color: #1e3a8a; font-weight: 600; cursor: pointer; border-radius: 4px;
}
.breadcrumb-back:hover { background: #eef2ff; }
.breadcrumb-sep { color: #cbd5e1; font-size: 12px; }
.breadcrumb-segment {
  background: transparent; border: 0; padding: 4px 8px; cursor: pointer;
  color: #64748b; font-size: 13px; border-radius: 4px;
}
.breadcrumb-segment:hover { background: #f1f5f9; color: #0f172a; }
.breadcrumb-segment.current { color: #0f172a; font-weight: 600; cursor: default; }
.breadcrumb-segment.current:hover { background: transparent; }

/* === CARDS livello (B drill-in elenco) === */
.cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; }
.gp-card {
  border: 1px solid var(--bop-color-border, #e2e8f0); border-radius: 8px; background: white;
  padding: 14px; cursor: pointer; transition: all .15s ease;
}
.gp-card:hover { border-color: #6366f1; box-shadow: 0 2px 8px rgba(99, 102, 241, 0.12); transform: translateY(-1px); }
.gp-card-header { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.gp-card-livello-badge {
  font-size: 10px; font-weight: 700; padding: 2px 6px; border-radius: 3px;
  background: #eef2ff; color: #4338ca; text-transform: uppercase;
}
.gp-card-title { flex: 1; font-weight: 600; color: #0f172a; }
.gp-card-meta {
  display: flex; gap: 12px; font-size: 12px; color: #64748b;
}
.gp-card-totale { font-weight: 600; color: #16a34a; font-variant-numeric: tabular-nums; }

/* === Toolbar gruppo === */
.gp-toolbar {
  display: flex; gap: 8px; padding: 8px 12px;
  background: #f8fafc; border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
}
.gp-toolbar button {
  background: white; border: 1px solid #cbd5e1; padding: 4px 10px;
  border-radius: 4px; font-size: 12px; cursor: pointer; display: inline-flex; gap: 4px; align-items: center;
}
.gp-toolbar button:hover { background: #eef2ff; border-color: #6366f1; color: #4338ca; }

/* === Footer totale === */
.totali-card {
  margin-top: 16px; padding: 14px 18px; background: #f0f9ff;
  border: 1px solid #bae6fd; border-radius: 8px;
  display: flex; justify-content: space-between; align-items: center;
}
.totali-label { font-size: 12px; text-transform: uppercase; letter-spacing: .05em; color: #075985; font-weight: 600; }
.totali-value { font-size: 22px; font-weight: 700; color: #0c4a6e; font-variant-numeric: tabular-nums; }
`;

// ── Componente riga (tabella) condiviso ─────────────────────────────────────

const RigheTable: React.FC<{ righe: Riga[] }> = ({ righe }) => {
  if (!righe.length) return null;

  // Raggruppa per cluster alternative (visivamente)
  const blocks: Array<{ kind: 'normal'; r: Riga } | { kind: 'cluster-header'; gruppo: string; numAlt: number; scelta?: string }> = [];
  let lastCluster: string | null = null;
  for (const r of righe) {
    if (r.alternativaGruppo && r.alternativaGruppo !== lastCluster) {
      const numAlt = righe.filter(x => x.alternativaGruppo === r.alternativaGruppo).length;
      const scelta = righe.find(x => x.alternativaGruppo === r.alternativaGruppo && x.alternativaScelta);
      blocks.push({ kind: 'cluster-header', gruppo: r.alternativaGruppo, numAlt, scelta: scelta?.descrizione });
      lastCluster = r.alternativaGruppo;
    } else if (!r.alternativaGruppo) {
      lastCluster = null;
    }
    blocks.push({ kind: 'normal', r });
  }

  return (
    <table className="righe-table">
      <thead>
        <tr>
          <th className="col-grip"></th>
          <th className="col-num">#</th>
          <th className="col-tipo">Tipo</th>
          <th className="col-codice">Codice</th>
          <th>Descrizione</th>
          <th className="col-um">U.M.</th>
          <th className="num">Qta</th>
          <th className="num">Prezzo</th>
          <th className="num">Sconto%</th>
          <th className="num">Importo</th>
          <th className="col-azioni"></th>
        </tr>
      </thead>
      <tbody>
        {blocks.map((b, i) => {
          if (b.kind === 'cluster-header') {
            return (
              <tr key={`c-${i}`} className="cluster-row">
                <td colSpan={11}>
                  <span className="alt-badge">ALT {b.gruppo}</span>
                  {b.numAlt} alternativ{b.numAlt === 1 ? 'a' : 'e'}
                  {b.scelta && <> · scelta: <strong>{b.scelta}</strong></>}
                </td>
              </tr>
            );
          }
          const r = b.r;
          const tipoCod = r.tipo === 'ARTICOLO' ? 'ART' : r.tipo === 'ARTICOLO_CONFIGURATO' ? 'CFG' : 'DSC';
          return (
            <tr key={r.id} className="riga-row">
              <td className="col-grip"><GripIcon /></td>
              <td className="col-num">{r.ordine}</td>
              <td className="col-tipo"><span className={`tipo-badge tipo-badge--${tipoCod}`}>{tipoCod}</span></td>
              <td className="col-codice">{r.codice || '—'}</td>
              <td>
                {r.descrizione}
                {r.alternativaScelta === false && <span className="bop-text-muted" style={{ marginLeft: 6 }}>(non scelta)</span>}
                {r.alternativaScelta === true && r.alternativaGruppo && <span className="scelta-badge">SCELTA</span>}
                {r.etichettaAlternativa && <span className="bop-text-muted" style={{ marginLeft: 6, fontSize: 11 }}>· {r.etichettaAlternativa}</span>}
              </td>
              <td>{r.um || '—'}</td>
              <td className="num">{fmt(r.qta)}</td>
              <td className="num">{fmt(r.prezzo)}</td>
              <td className="num">{r.sconto ? fmt(r.sconto) : '—'}</td>
              <td className="num"><strong>{fmt(r.importo)}</strong></td>
              <td className="col-azioni">
                <button className="bop-btn-icon" title="Modifica"><EditIcon /></button>
                <button className="bop-btn-icon" title="Elimina"><TrashIcon /></button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// ── PROPOSTA A — Accordion completo ─────────────────────────────────────────

const ProposalAccordion: React.FC = () => {
  const [openL1, setOpenL1] = useState<Set<number>>(new Set([1, 2]));
  const [openL2, setOpenL2] = useState<Set<number>>(new Set([11, 12, 21, 22]));
  const [openL3, setOpenL3] = useState<Set<number>>(new Set([111, 221, 222]));

  const toggle = (s: Set<number>, setS: (n: Set<number>) => void, id: number) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    setS(next);
  };

  const renderL3 = (g: Gruppo) => {
    const isOpen = openL3.has(g.id);
    return (
      <div key={g.id} className="gp-l3">
        <div className="gp-header" onClick={() => toggle(openL3, setOpenL3, g.id)}>
          <span className="gp-grip" onClick={e => e.stopPropagation()}><GripIcon /></span>
          <span className="gp-chevron">{isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}</span>
          <span className="gp-label">{g.etichetta}</span>
          <span className="gp-meta">{g.righe.length} righe · € {fmt(importoRicorsivo(g.id))}</span>
          <span className="gp-actions" onClick={e => e.stopPropagation()}>
            <button className="bop-btn-icon" title="Rinomina"><EditIcon /></button>
            <button className="bop-btn-icon" title="Elimina"><TrashIcon /></button>
          </span>
        </div>
        {isOpen && (
          <div className="gp-body">
            {g.righe.length > 0 ? <RigheTable righe={g.righe} /> : <div className="gp-empty">Nessuna riga</div>}
            <div className="gp-empty-add"><PlusIcon /> Aggiungi riga in "{g.etichetta}"</div>
          </div>
        )}
      </div>
    );
  };

  const renderL2 = (g: Gruppo) => {
    const isOpen = openL2.has(g.id);
    const figliL3 = figliDi(g.id, 3);
    return (
      <div key={g.id} className="gp-l2">
        <div className="gp-header" onClick={() => toggle(openL2, setOpenL2, g.id)}>
          <span className="gp-grip" onClick={e => e.stopPropagation()}><GripIcon /></span>
          <span className="gp-chevron">{isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}</span>
          <span className="gp-label">{g.etichetta}</span>
          <span className="gp-meta">{totaleRigheRicorsivo(g.id)} righe · € {fmt(importoRicorsivo(g.id))}</span>
          <span className="gp-actions" onClick={e => e.stopPropagation()}>
            <button className="bop-btn-icon" title="Rinomina"><EditIcon /></button>
            <button className="bop-btn-icon" title="Elimina"><TrashIcon /></button>
          </span>
        </div>
        {isOpen && (
          <div className="gp-body gp-body--nested">
            {g.righe.length > 0 && <RigheTable righe={g.righe} />}
            {figliL3.map(renderL3)}
            <div className="gp-empty-add"><PlusIcon /> Aggiungi sezione in "{g.etichetta}" o riga diretta</div>
          </div>
        )}
      </div>
    );
  };

  const renderL1 = (g: Gruppo) => {
    const isOpen = openL1.has(g.id);
    const figliL2 = figliDi(g.id, 2);
    return (
      <div key={g.id} className="gp-l1">
        <div className="gp-header" onClick={() => toggle(openL1, setOpenL1, g.id)}>
          <span className="gp-grip" onClick={e => e.stopPropagation()}><GripIcon /></span>
          <span className="gp-chevron">{isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}</span>
          <span className="gp-label">{g.etichetta}</span>
          <span className="gp-meta">{totaleRigheRicorsivo(g.id)} righe · € {fmt(importoRicorsivo(g.id))}</span>
          <span className="gp-actions" onClick={e => e.stopPropagation()}>
            <button className="bop-btn-icon" title="Rinomina"><EditIcon /></button>
            <button className="bop-btn-icon" title="Elimina"><TrashIcon /></button>
          </span>
        </div>
        {isOpen && (
          <div className="gp-body gp-body--nested">
            {g.righe.length > 0 && <RigheTable righe={g.righe} />}
            {figliL2.map(renderL2)}
            <div className="gp-empty-add"><PlusIcon /> Aggiungi locale in "{g.etichetta}"</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="mockup-note">
        <strong>Proposta A — Accordion completo.</strong> Tutta la struttura visibile in pagina.
        Ogni livello ha chevron per espandere/comprimere. Adatto a preventivi medi/piccoli.
        Quando si lavora a un preventivo grande, scorrere l'intera pagina può diventare lungo.
      </div>
      {figliDi(null, 1).map(renderL1)}
      <div className="gp-empty-add" style={{ marginTop: 8 }}>
        <PlusIcon /> Aggiungi macro-area (livello 1)
      </div>
    </div>
  );
};

// ── PROPOSTA B — Drill-in puro con breadcrumb ───────────────────────────────

const ProposalDrillIn: React.FC = () => {
  // Stato: in quale gruppo siamo "entrati". null = radice (mostro le L1)
  const [path, setPath] = useState<number[]>([]);

  const pathLabels = useMemo(() => {
    return path.map(id => GRUPPI.find(g => g.id === id)!).filter(Boolean) as Gruppo[];
  }, [path]);

  const currentParentId = path.length === 0 ? null : path[path.length - 1];
  const currentLevel: 1 | 2 | 3 = path.length === 0 ? 1 : (path.length === 1 ? 2 : 3);
  const currentGroup = currentParentId ? GRUPPI.find(g => g.id === currentParentId) : null;
  const childGroups = figliDi(currentParentId, currentLevel);
  const directRighe = currentGroup?.righe || [];

  const enterGroup = (id: number) => {
    if (path.length >= 2) return; // non si scende oltre L3
    setPath([...path, id]);
  };
  const goTo = (idx: number) => setPath(path.slice(0, idx + 1));
  const goBack = () => setPath(path.slice(0, -1));

  return (
    <div>
      <div className="mockup-note">
        <strong>Proposta B — Drill-in puro.</strong> Navighi "dentro" un gruppo come in
        un esplora-cartelle. La pagina mostra solo il livello in cui ti trovi. Breadcrumb
        per tornare indietro. Niente accordion: scorrimento minimo, focus massimo sul contesto attuale.
      </div>

      {/* Breadcrumb */}
      <div className="breadcrumb-bar">
        {path.length > 0 && (
          <>
            <button className="breadcrumb-back" onClick={goBack}>
              <ArrowLeftIcon /> Indietro
            </button>
            <span className="breadcrumb-sep">|</span>
          </>
        )}
        <button className={`breadcrumb-segment ${path.length === 0 ? 'current' : ''}`} onClick={() => setPath([])}>
          📋 Radice preventivo
        </button>
        {pathLabels.map((g, i) => (
          <React.Fragment key={g.id}>
            <span className="breadcrumb-sep">›</span>
            <button
              className={`breadcrumb-segment ${i === pathLabels.length - 1 ? 'current' : ''}`}
              onClick={() => goTo(i)}
            >
              {g.etichetta}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Contenuto: cards dei figli di livello successivo + righe dirette */}
      {childGroups.length > 0 && (
        <>
          <h4 className="mockup-section-title">
            {currentLevel === 1 && 'Macro-aree'}
            {currentLevel === 2 && `Locali in "${currentGroup?.etichetta}"`}
            {currentLevel === 3 && `Sezioni in "${currentGroup?.etichetta}"`}
          </h4>
          <div className="cards-grid" style={{ marginBottom: 16 }}>
            {childGroups.map(g => (
              <div key={g.id} className="gp-card" onClick={() => enterGroup(g.id)}>
                <div className="gp-card-header">
                  <span className="gp-grip" onClick={e => e.stopPropagation()}><GripIcon /></span>
                  <span className="gp-card-livello-badge">L{g.livello}</span>
                  <span className="gp-card-title">{g.etichetta}</span>
                </div>
                <div className="gp-card-meta">
                  <span>{totaleRigheRicorsivo(g.id)} righe</span>
                  <span>{figliDi(g.id, (g.livello + 1) as any).length} sotto-gruppi</span>
                  <span className="gp-card-totale">€ {fmt(importoRicorsivo(g.id))}</span>
                </div>
              </div>
            ))}
            <div className="gp-card" style={{ borderStyle: 'dashed', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#64748b', fontSize: 13 }}>
                <PlusIcon /> Aggiungi {currentLevel === 1 ? 'macro-area' : currentLevel === 2 ? 'locale' : 'sezione'}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Righe dirette del gruppo corrente */}
      {currentGroup && (
        <>
          <h4 className="mockup-section-title">Righe dirette in "{currentGroup.etichetta}"</h4>
          <div className="gp-l3" style={{ borderLeftColor: '#0ea5e9' }}>
            <div className="gp-toolbar">
              <button><PlusIcon /> Aggiungi riga</button>
              <button><PlusIcon /> Aggiungi descrittiva</button>
            </div>
            <div className="gp-body">
              {directRighe.length > 0 ? (
                <RigheTable righe={directRighe} />
              ) : (
                <div className="gp-empty">Nessuna riga diretta in questo {currentLevel === 1 ? 'macro-area' : currentLevel === 2 ? 'locale' : 'sezione'}.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ── PROPOSTA C — Misto: accordion + bottone "focalizza" ─────────────────────

const ProposalHybrid: React.FC = () => {
  const [focusId, setFocusId] = useState<number | null>(null);
  const [openL1, setOpenL1] = useState<Set<number>>(new Set([1, 2]));
  const [openL2, setOpenL2] = useState<Set<number>>(new Set([11, 12, 21, 22]));
  const [openL3, setOpenL3] = useState<Set<number>>(new Set([111, 221, 222]));

  const toggle = (s: Set<number>, setS: (n: Set<number>) => void, id: number) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    setS(next);
  };

  // Path breadcrumb se in focus
  const focusPath: Gruppo[] = useMemo(() => {
    if (!focusId) return [];
    const out: Gruppo[] = [];
    let cur = GRUPPI.find(g => g.id === focusId);
    while (cur) {
      out.unshift(cur);
      cur = cur.parentId ? GRUPPI.find(g => g.id === cur!.parentId) : undefined;
    }
    return out;
  }, [focusId]);

  // Quando focus è attivo, renderizzo solo il sottoalbero di quel gruppo
  const isInFocus = (g: Gruppo): boolean => {
    if (!focusId) return true;
    if (g.id === focusId) return true;
    let cur = g.parentId;
    while (cur) {
      if (cur === focusId) return true;
      const p = GRUPPI.find(x => x.id === cur)!;
      cur = p.parentId;
    }
    return false;
  };

  const renderL3 = (g: Gruppo) => {
    if (!isInFocus(g)) return null;
    const isOpen = openL3.has(g.id);
    return (
      <div key={g.id} className="gp-l3">
        <div className="gp-header" onClick={() => toggle(openL3, setOpenL3, g.id)}>
          <span className="gp-grip" onClick={e => e.stopPropagation()}><GripIcon /></span>
          <span className="gp-chevron">{isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}</span>
          <span className="gp-label">{g.etichetta}</span>
          <span className="gp-meta">{g.righe.length} righe</span>
          <span className="gp-actions" onClick={e => e.stopPropagation()}>
            <button className="bop-btn-icon" title="Focalizza" onClick={() => setFocusId(g.id)}><FocusIcon /></button>
            <button className="bop-btn-icon" title="Rinomina"><EditIcon /></button>
            <button className="bop-btn-icon" title="Elimina"><TrashIcon /></button>
          </span>
        </div>
        {isOpen && (
          <div className="gp-body">
            {g.righe.length > 0 ? <RigheTable righe={g.righe} /> : <div className="gp-empty">Nessuna riga</div>}
          </div>
        )}
      </div>
    );
  };

  const renderL2 = (g: Gruppo) => {
    if (!isInFocus(g)) return null;
    const isOpen = openL2.has(g.id);
    return (
      <div key={g.id} className="gp-l2">
        <div className="gp-header" onClick={() => toggle(openL2, setOpenL2, g.id)}>
          <span className="gp-grip" onClick={e => e.stopPropagation()}><GripIcon /></span>
          <span className="gp-chevron">{isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}</span>
          <span className="gp-label">{g.etichetta}</span>
          <span className="gp-meta">{totaleRigheRicorsivo(g.id)} righe · € {fmt(importoRicorsivo(g.id))}</span>
          <span className="gp-actions" onClick={e => e.stopPropagation()}>
            <button className="bop-btn-icon" title="Focalizza su questo locale" onClick={() => setFocusId(g.id)}><FocusIcon /></button>
            <button className="bop-btn-icon" title="Rinomina"><EditIcon /></button>
            <button className="bop-btn-icon" title="Elimina"><TrashIcon /></button>
          </span>
        </div>
        {isOpen && (
          <div className="gp-body gp-body--nested">
            {g.righe.length > 0 && <RigheTable righe={g.righe} />}
            {figliDi(g.id, 3).map(renderL3)}
          </div>
        )}
      </div>
    );
  };

  const renderL1 = (g: Gruppo) => {
    if (!isInFocus(g)) return null;
    const isOpen = openL1.has(g.id);
    return (
      <div key={g.id} className="gp-l1">
        <div className="gp-header" onClick={() => toggle(openL1, setOpenL1, g.id)}>
          <span className="gp-grip" onClick={e => e.stopPropagation()}><GripIcon /></span>
          <span className="gp-chevron">{isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}</span>
          <span className="gp-label">{g.etichetta}</span>
          <span className="gp-meta">{totaleRigheRicorsivo(g.id)} righe · € {fmt(importoRicorsivo(g.id))}</span>
          <span className="gp-actions" onClick={e => e.stopPropagation()}>
            <button className="bop-btn-icon" title="Focalizza su questa macro-area" onClick={() => setFocusId(g.id)}><FocusIcon /></button>
            <button className="bop-btn-icon" title="Rinomina"><EditIcon /></button>
            <button className="bop-btn-icon" title="Elimina"><TrashIcon /></button>
          </span>
        </div>
        {isOpen && (
          <div className="gp-body gp-body--nested">
            {g.righe.length > 0 && <RigheTable righe={g.righe} />}
            {figliDi(g.id, 2).map(renderL2)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="mockup-note">
        <strong>Proposta C — Misto.</strong> Comportamento default come la A (accordion completo).
        Ogni gruppo ha un'icona "🎯 focalizza" che, se cliccata, isola visivamente quel sotto-albero
        (tutto il resto sparisce). Per uscire dal focus c'è il breadcrumb in cima.
        Ottieni il meglio dei due mondi: vista d'insieme + capacità di "zoomare" su un'area specifica.
      </div>

      {/* Breadcrumb visibile solo in modalità focus */}
      {focusId && (
        <div className="breadcrumb-bar">
          <button className="breadcrumb-back" onClick={() => setFocusId(null)}>
            <ArrowLeftIcon /> Esci da focus
          </button>
          <span className="breadcrumb-sep">|</span>
          <span style={{ fontSize: 12, color: '#64748b' }}>Stai vedendo solo:</span>
          {focusPath.map((g, i) => (
            <React.Fragment key={g.id}>
              {i > 0 && <span className="breadcrumb-sep">›</span>}
              <button
                className={`breadcrumb-segment ${i === focusPath.length - 1 ? 'current' : ''}`}
                onClick={() => setFocusId(g.id)}
              >
                {g.etichetta}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {figliDi(null, 1).map(renderL1)}

      {!focusId && (
        <div className="gp-empty-add" style={{ marginTop: 8 }}>
          <PlusIcon /> Aggiungi macro-area (livello 1)
        </div>
      )}
    </div>
  );
};

// ── Pagina principale ───────────────────────────────────────────────────────

const MockupRighePreventivoDrilldown: React.FC = () => {
  const [tab, setTab] = useState<'A' | 'B' | 'C'>('A');

  const totaleGenerale = figliDi(null, 1).reduce((sum, g) => sum + importoRicorsivo(g.id), 0);

  return (
    <Layout>
      <style>{STYLE_BLOCK}</style>
      <div className="mockup-drilldown">
        <div className="bop-page-header">
          <h1 className="bop-page-title">Mockup — Righe preventivo, drill-down a 3 livelli</h1>
        </div>

        <p className="bop-text-muted" style={{ maxWidth: 760, marginTop: 0 }}>
          Tre proposte di interazione per gestire raggruppamenti su 3 livelli (es. Macro-area / Locale / Sezione)
          con drag&amp;drop totale di righe e gruppi. Etichette libere. Lo switch in alto cambia la vista.
          Dataset condiviso: una commessa Ceramita di esempio.
        </p>

        <div className="mockup-switch">
          <button className={tab === 'A' ? 'active' : ''} onClick={() => setTab('A')}>A — Accordion completo</button>
          <button className={tab === 'B' ? 'active' : ''} onClick={() => setTab('B')}>B — Drill-in puro</button>
          <button className={tab === 'C' ? 'active' : ''} onClick={() => setTab('C')}>C — Misto (accordion + focalizza)</button>
        </div>

        {tab === 'A' && <ProposalAccordion />}
        {tab === 'B' && <ProposalDrillIn />}
        {tab === 'C' && <ProposalHybrid />}

        <div className="totali-card">
          <div>
            <div className="totali-label">Totale preventivo (vista cliente)</div>
            <div style={{ fontSize: 11, color: '#075985' }}>somma di tutte le righe valide nei 3 livelli</div>
          </div>
          <div className="totali-value">€ {fmt(totaleGenerale)}</div>
        </div>
      </div>
    </Layout>
  );
};

export default MockupRighePreventivoDrilldown;
